import React from 'react';
import {
  HStack,
  Button,
  Text,
  Select,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaChevronLeft, FaChevronRight, FaAngleDoubleLeft, FaAngleDoubleRight } from 'react-icons/fa';

/**
 * کامپوننت Pagination
 * @param {Object} props - props کامپوننت
 * @param {number} props.currentPage - صفحه فعلی
 * @param {number} props.totalPages - تعداد کل صفحات
 * @param {number} props.totalItems - تعداد کل آیتم‌ها
 * @param {number} props.itemsPerPage - تعداد آیتم در هر صفحه
 * @param {Function} props.onPageChange - تابع تغییر صفحه
 * @param {Function} props.onItemsPerPageChange - تابع تغییر تعداد آیتم در هر صفحه
 * @param {boolean} props.isLoading - وضعیت بارگذاری
 */
const Pagination = ({
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  itemsPerPage = 10,
  onPageChange,
  onItemsPerPageChange,
  isLoading = false,
}) => {
  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const bgColor = useColorModeValue("white", "gray.700");

  // محاسبه محدوده آیتم‌های نمایش داده شده
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // تولید آرایه صفحات برای نمایش
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // اگر تعداد صفحات کم است، همه را نمایش بده
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // نمایش صفحات هوشمند
      if (currentPage <= 3) {
        // صفحات ابتدایی
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // صفحات انتهایی
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // صفحات میانی
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const handlePageChange = (page) => {
    if (page !== currentPage && page >= 1 && page <= totalPages && !isLoading) {
      onPageChange(page);
    }
  };

  const handleItemsPerPageChange = (event) => {
    const newItemsPerPage = parseInt(event.target.value);
    if (onItemsPerPageChange && newItemsPerPage !== itemsPerPage) {
      onItemsPerPageChange(newItemsPerPage);
    }
  };

  if (totalPages <= 1 && totalItems <= itemsPerPage) {
    return null; // اگر pagination نیاز نیست، چیزی نمایش نده
  }

  return (
    <HStack
      justify="space-between"
      align="center"
      w="full"
      p={4}
      borderTop="1px"
      borderColor={borderColor}
      bg={bgColor}
    >
      {/* اطلاعات تعداد آیتم‌ها */}
      <Text fontSize="sm" color={textColor}>
        نمایش {startItem} تا {endItem} از {totalItems} آیتم
      </Text>

      {/* کنترل‌های Pagination */}
      <HStack spacing={2}>
        {/* انتخاب تعداد آیتم در هر صفحه */}
        <HStack spacing={2}>
          <Text fontSize="sm" color={textColor}>
            در هر صفحه:
          </Text>
          <Select
            size="sm"
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            w="70px"
            isDisabled={isLoading}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </Select>
        </HStack>

        {/* دکمه‌های Pagination */}
        <HStack spacing={1}>
          {/* دکمه رفتن به صفحه اول */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => handlePageChange(1)}
            isDisabled={currentPage === 1 || isLoading}
            leftIcon={<FaAngleDoubleRight />}
            aria-label="صفحه اول"
          />

          {/* دکمه صفحه قبل */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => handlePageChange(currentPage - 1)}
            isDisabled={currentPage === 1 || isLoading}
            leftIcon={<FaChevronRight />}
            aria-label="صفحه قبل"
          />

          {/* شماره صفحات */}
          {getPageNumbers().map((page, index) => (
            <Button
              key={index}
              size="sm"
              variant={page === currentPage ? "solid" : "outline"}
              colorScheme={page === currentPage ? "blue" : "gray"}
              onClick={() => typeof page === 'number' && handlePageChange(page)}
              isDisabled={page === '...' || isLoading}
              minW="40px"
            >
              {page}
            </Button>
          ))}

          {/* دکمه صفحه بعد */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => handlePageChange(currentPage + 1)}
            isDisabled={currentPage === totalPages || isLoading}
            rightIcon={<FaChevronLeft />}
            aria-label="صفحه بعد"
          />

          {/* دکمه رفتن به صفحه آخر */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => handlePageChange(totalPages)}
            isDisabled={currentPage === totalPages || isLoading}
            rightIcon={<FaAngleDoubleLeft />}
            aria-label="صفحه آخر"
          />
        </HStack>
      </HStack>
    </HStack>
  );
};

export default Pagination;
